import * as moment from "moment";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import { Paths } from "../../../constants";
import { CareerCenterStatuses } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import Settings from "../../../settings";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    goto: (url: string) => {
        dispatch(push(url));
    },
    notifyInfo: (message: string, icon?: string) => dispatch(Actions.Noti.notifyInfo(message, icon)),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {
};

interface ConnectedState {
    api: Api;
}

interface ConnectedDispatch {
    goto: (url: string) => void;
    notifyInfo: (message: string, icon?: string) => void;
}

interface OwnState {
    errors: string[];
    jobPost?: M.CareerCenter.Resume;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ReviewResumeComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);
        this.state = {
            errors: [],
            loading: true,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        const guid = this.props.match.params.guid;
        if (!guid) {
            this.setState({
                ...this.state,
                errors: ["Resume identifier is missing in URL (guid)"],
            });
            return;
        }

        const qp = { queryParams: { guid } };
        const result = await this.props.api.get<M.CareerCenter.Resume>(Api.endpoints.users.careerCenter.resume, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            if (result.data.statusId !== CareerCenterStatuses.Created.id) {
                this.props.notifyInfo("The requested resume has already been posted.");
                this.props.goto(Paths.CareerCenter.Resume.Confirmation + "/" + guid);
                return;
            }

            this.setState({
                ...this.state,
                jobPost: result.data,
            });
        }
        else {
            const errors =  M.Result.Result.errorsOrMessage(result);
            this.setState({
                ...this.state,
                errors,
            });
        }

        this.toggleLoading(false);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        let details = null;

        if (this.state.errors.length) {
            details = <BS.Alert bsStyle="warning">
                <h3>The requested resume could not be retrieved.</h3>
                <ul>
                    {this.state.errors.map((e, i) => (<li key={i}>{e.toString()}</li>))}
                </ul>
            </BS.Alert>;
        }
        else if (this.state.jobPost) {
                details = <ReviewResumeForm
                api={this.props.api}
                resume={this.state.jobPost}
                onSuccess={(model) => this.onFinalizeSuccess(model)}
            />;
        }

        return <Comp.Page.PublicPage pageTitle="Review resume" style={{position: "relative", minHeight: "300px"}}>
            <Comp.Generic.LoadingSpinner show={this.state.loading} />
            <h1>Review</h1>
            {details}
        </Comp.Page.PublicPage>;
    }

    private onFinalizeSuccess(job: M.CareerCenter.Resume) {
        this.props.goto(`${Paths.CareerCenter.Resume.Confirmation}/${job.guid}`);
    }
}

export const ReviewResume: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ReviewResumeComponent) as any);

interface ReviewResumeFormProps {
    api: Api;
    resume: M.CareerCenter.Resume;
    onSuccess: (job: M.CareerCenter.Resume) => void;
}

class ReviewResumeForm extends ComponentWithValidatedForm<ReviewResumeFormProps, {}> {

    private _isMounted = false;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.CareerCenter.ReviewResume();
        formModel.data.guid = this.props.resume.guid;
        this.state = {
            ...this.getInitialState(formModel),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const resume = this.props.resume;
        const editLink = <Link to={Paths.CareerCenter.Resume.Post + "/" + resume.guid}>Edit</Link>;

        let educations = [<Comp.Editors.FormField name="" key={0}>None &ndash; {editLink}</Comp.Editors.FormField>];
        let experiences = [<Comp.Editors.FormField name="" key={0}>None &ndash; {editLink}</Comp.Editors.FormField>];
        let relPrefs = [<Comp.Editors.FormField name="" key={0}>None &ndash; {editLink}</Comp.Editors.FormField>];

        if (resume.educations.length) {
            educations = resume.educations.map((e, i) => (
                <Comp.Editors.FormField name="" label={e.type || "Other"} key={i}>
                    {e.organization}, {e.completedOn ? moment(Utility.fromApiDate(e.completedOn)).format("MMM YYYY") : "Incomplete"} &ndash; {editLink}
                </Comp.Editors.FormField>
            ));
        }

        if (resume.experiences.length) {
            experiences = resume.experiences.map((e, i) => (
                <Comp.Editors.FormField name="" label={"Experience #" + (i + 1)} key={i}>
                    {e.position} at {e.organization} &ndash; {editLink}
                </Comp.Editors.FormField>
            ));
        }

        if (resume.relocationPrefs.length) {
            relPrefs = resume.relocationPrefs.map((r, i) => (
                <Comp.Editors.FormField name="" label={"Location #" + (i + 1)} key={i}>
                    {M.Generic.AddressHelper.displayInline(r.location, "iso3")} &ndash; {editLink}
                </Comp.Editors.FormField>
            ));
        }

        return <div className="job-post">
            <h2>Position Details</h2>

            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                schema={this.state.formModel.rules}
                onValidSubmit={(model) => this.onSubmit(model)}
            >
                <Comp.Editors.Hidden formComponent={this} name="guid" />

                <h2>CV/Resume</h2>

                <Comp.Editors.FormField name="" label="Document">
                    {resume.cvMedia ? <a href={resume.cvMedia.url} target="_blank">{resume.cvMedia.fileName}</a> : "Not available"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <hr />
                <h2>Desired Position</h2>

                <Comp.Editors.FormField name="" label="Job category">
                    {resume.category || "Not available"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="" label="Position title">
                    {resume.title || "Not available"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <hr />
                <h2>Cover Letter</h2>

                <Comp.Editors.FormField name="" label="Body">
                    <div dangerouslySetInnerHTML={{ __html: resume.coverLetter || "Not available" }} />
                    {editLink}
                </Comp.Editors.FormField>

                <hr />
                <h2>Education &amp; Training</h2>

                {educations}

                <hr />
                <h2>Experience</h2>

                {experiences}

                <hr />
                <h2>Relocation Preference(s)</h2>

                {relPrefs}

                <hr />
                <h2>Job Alerts</h2>

                <Comp.Editors.FormField name="" label="Subscribe">
                    {resume.subscribed ? "Yes" : "No"} &ndash; {editLink}
                </Comp.Editors.FormField>

                <Comp.Editors.FormField name="">
                    By pressing submit, you agree that this job listing will be posted on the {Settings.ClientNameAbbreviation} Career Center in accordance
                    with {Settings.ClientName} <Link to={Settings.AsrsLegacyUrl + Paths.Page.PrivacyPolicy}>Privacy Policy</Link> and
                    <Link to={Settings.AsrsLegacyUrl + Paths.Page.TermsOfUse}>Terms of Use</Link>.
                </Comp.Editors.FormField>

                <Comp.Editors.SubmitField
                    cancelLink={Paths.Home}
                    showProcessing={this.state.formProcessing}
                />
            </Comp.Validation.ValidatedForm>
        </div>;
    }

    private async onSubmit(model: Forms.CareerCenter.ReviewResumeData) {
        this.toggleProcessing(true);

        const qp = { queryParams: { guid: model.guid }};
        const result = await this.props.api.post<M.CareerCenter.Resume>(Api.endpoints.users.careerCenter.finalizeResume, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.props.onSuccess(result.data);
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }

        if (this._isMounted)
            this.toggleProcessing(false);
    }
}



// WEBPACK FOOTER //
// ./src/containers/careerCenter/reviewResume/ReviewResume.tsx